import { useState } from "react";
import { Trash2, Minus, Plus, Search, Check, ChevronDown, ChevronRight, Star, Image as ImageIcon, Copy, Eye } from "lucide-react";
import { pln, API } from "@/lib/api";
import { commissionPerPiece, tierChips, haftSurcharge } from "@/lib/commission";
import { colorCode, colorName, colorLabel, colorHex } from "@/lib/colors";
import { ColorSwatch } from "@/components/ColorSwatch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export const ItemRow = ({ idx, item, products, catalog = { branding_types: [], placements: [] }, onChange, onRemove, onDuplicate, canRemove, vizOptions = [], rules = {}, isAdmin = false }) => {
  const prod = products.find((p) => p.sku === item.sku);
  const [skuOpen, setSkuOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [open, setOpen] = useState(true);
  const [vizOpen, setVizOpen] = useState(false);
  const [previewViz, setPreviewViz] = useState(null);
  const selectedViz = vizOptions.find((v) => v.id === item.viz_id);
  const sizes = prod?.sizes || [];
  const qty = Object.values(item.sizes).reduce((a, b) => a + (Number(b) || 0), 0);
  const bTypes = (prod?.branding_types?.length ? prod.branding_types : catalog.branding_types) || [];
  const pOpts = Array.from(new Set([...(prod?.placements || []), ...(catalog.placements || [])]));
  const br = item.branding || { type: bTypes[0] || "DTF", placements: [] };
  const setBrandingType = (t) => onChange({ ...item, branding: { type: t, placements: item.branding?.placements || [] } });
  const togglePlacement = (p) => { const curp = item.branding?.placements || []; onChange({ ...item, branding: { type: br.type, placements: curp.includes(p) ? curp.filter((x) => x !== p) : [...curp, p] } }); };
  const c = commissionPerPiece(item.sku, item.unit_price, br.type, br.placements, rules);
  const customComm = !!item.custom_commission;
  const setCustomComm = (on) => onChange({ ...item, custom_commission: on, custom_commission_per_piece: on ? (Number(item.custom_commission_per_piece) || 0) : 0 });
  const setCustomCommPrice = (v) => onChange({ ...item, custom_commission: true, custom_commission_per_piece: v === "" ? 0 : Math.max(0, Number(v)) });
  const effCommPP = customComm ? (Number(item.custom_commission_per_piece) || 0) : c.commission;
  const effCommNote = customComm ? "" : c.note;
  const price = Number(item.unit_price) || 0;
  const rule = rules[item.sku] || {};
  const surcharge = rule.size_surcharge || null;
  const isHaft = (br.type || "").toLowerCase().includes("haft");
  const haft = rule.haft || { przod: 15, tyl: 35, oba: 45 };
  const haftCustom = !!br.haft_custom;
  const haftAdd = isHaft ? (haftCustom ? (Number(br.haft_custom_price) || 0) : haftSurcharge(item.sku, br.type, br.placements || [], rules)) : 0;
  const unitTotal = price + haftAdd;
  const setHaftCustom = (on) => onChange({ ...item, branding: { ...br, type: br.type, placements: br.placements || [], haft_custom: on } });
  const setHaftCustomPrice = (v) => onChange({ ...item, branding: { ...br, type: br.type, placements: br.placements || [], haft_custom: true, haft_custom_price: v === "" ? 0 : Math.max(0, Number(v)) } });

  const setSku = (sku) => {
    const p = products.find((x) => x.sku === sku);
    onChange({ ...item, sku, color: p.colors[0], sizes: {} });
  };
  const cur = (s) => Number(item.sizes[s]) || 0;
  const setSize = (s, val) => onChange({ ...item, sizes: { ...item.sizes, [s]: val === "" ? "" : Math.max(0, val) } });

  const POPULAR = ["BLACK", "WHITE", "GREY", "GRAY", "SILVER"];
  const rank = (col) => {
    const c = col.toUpperCase();
    for (let i = 0; i < POPULAR.length; i++) if (c === POPULAR[i] || c.includes(POPULAR[i])) return i;
    return POPULAR.length;
  };
  const sortedColors = [...(prod?.colors || [])].sort((a, b) => rank(a) - rank(b));
  const isJhk = (prod?.brand || "").toUpperCase().includes("JHK");

  return (
    <div data-testid={`item-row-${idx}`} className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button type="button" data-testid={`item-toggle-${idx}`} onClick={() => setOpen((v) => !v)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <span className="font-mono text-xs font-bold text-amber-400">POZYCJA {idx + 1}</span>
          {!open && (
            <span className="flex min-w-0 items-center gap-2 truncate">
              <span className="truncate text-sm font-semibold">{item.sku || "—"}</span>
              {item.color && <span className="shrink-0 rounded-md bg-secondary px-2 py-0.5 text-xs font-semibold">{item.color}</span>}
              <span className="shrink-0 font-display text-sm font-extrabold text-amber-300">{qty} szt.</span>
            </span>
          )}
        </button>
        {onDuplicate && <Button data-testid={`item-duplicate-${idx}`} size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-sky-400" onClick={onDuplicate} title="Duplikuj pozycję (ten sam produkt, inna wizualizacja)"><Copy className="h-4 w-4" /></Button>}
        {canRemove && <Button data-testid={`item-remove-${idx}`} size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-rose-400" onClick={onRemove}><Trash2 className="h-4 w-4" /></Button>}
      </div>

      {open && (<>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold">Model</Label>
          <Popover open={skuOpen} onOpenChange={setSkuOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid={`item-sku-${idx}`} className="mt-1 flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400">
                <span className="truncate flex items-center gap-2"><span className="font-mono text-xs font-bold text-amber-500">{prod?.type}</span>{item.sku || "Wybierz model"}</span>
                <Search className="h-4 w-4 shrink-0 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[280px]">
              <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                <CommandInput data-testid={`item-sku-search-${idx}`} placeholder="Szukaj: nazwa, SKU, typ…" />
                <CommandList>
                  <CommandEmpty>Brak wyników.</CommandEmpty>
                  <CommandGroup>
                    {[...products].sort((a, b) => ((rules[b.sku]?.tiers || []).length ? 1 : 0) - ((rules[a.sku]?.tiers || []).length ? 1 : 0)).map((p) => {
                      const premium = (rules[p.sku]?.tiers || []).length > 0;
                      return (
                      <CommandItem key={p.sku} value={`${p.sku} ${p.name} ${p.type}`} data-testid={`item-sku-option-${p.sku}`}
                        onSelect={() => { setSku(p.sku); setSkuOpen(false); }}>
                        {premium
                          ? <Star className="mr-1.5 h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
                          : <span className="mr-1.5 w-3.5 shrink-0" />}
                        <span className="font-mono text-xs font-bold text-amber-500 mr-2 w-16 shrink-0">{p.type}</span>
                        <span className={`font-semibold ${premium ? "text-amber-200" : ""}`}>{p.sku}</span>
                        <span className="ml-2 truncate text-xs text-muted-foreground">{p.name}</span>
                        {item.sku === p.sku && <Check className="ml-auto h-4 w-4 text-amber-400" />}
                      </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-[11px] font-medium text-muted-foreground mt-1">{prod?.name}</p>
        </div>
        <div>
          <Label className="text-xs font-semibold">Kolor</Label>
          <Popover open={colorOpen} onOpenChange={setColorOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid={`item-color-${idx}`} className="mt-1 flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400">
                <span className="flex min-w-0 items-center gap-2 truncate">
                  {item.color && <ColorSwatch name={item.color} />}
                  <span className="truncate">{item.color ? (isJhk ? colorLabel(item.color) : colorName(item.color)) : "Wybierz kolor"}</span>
                </span>
                <Search className="h-4 w-4 shrink-0 opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]">
              <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                <CommandInput data-testid={`item-color-search-${idx}`} placeholder={isJhk ? "Szukaj koloru lub symbolu (np. CR, Canary)…" : "Szukaj koloru…"} />
                <CommandList>
                  <CommandEmpty>Brak wyników.</CommandEmpty>
                  <CommandGroup>
                    {sortedColors.map((col) => (
                      <CommandItem key={col} value={isJhk ? `${colorCode(col)} ${col}` : col} data-testid={`item-color-option-${col}`}
                        onSelect={() => { onChange({ ...item, color: col }); setColorOpen(false); }}>
                        <ColorSwatch name={col} className="mr-2" />
                        {isJhk && (colorCode(col)
                          ? <span className="mr-2 inline-flex min-w-[38px] justify-center rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px] font-bold text-amber-400">{colorCode(col)}</span>
                          : <span className="mr-2 min-w-[38px]" />)}
                        <span className="font-medium">{colorName(col)}</span>
                        {item.color === col && <Check className="ml-auto h-4 w-4 text-amber-400" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-xs font-semibold">Cena sprzedaży netto / szt. (PLN)</Label>
          <input data-testid={`item-price-${idx}`} type="number" min="0" step="0.01" value={item.unit_price}
            onChange={(e) => onChange({ ...item, unit_price: e.target.value })}
            className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 font-mono text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold">Rozmiary (ilości)</Label>
        <div className="mt-2 flex flex-wrap gap-2.5" data-testid={`size-matrix-${idx}`}>
          {sizes.map((s) => {
            const active = cur(s) > 0;
            return (
              <div key={s} className={`w-[88px] rounded-2xl p-2.5 flex flex-col items-center gap-2 transition-all ${active ? "bg-amber-400/10 ring-1 ring-amber-400/50" : "bg-background/60"}`}>
                <span className="text-[11px] font-bold tracking-wide text-muted-foreground">{s}</span>
                <input data-testid={`size-${idx}-${s}`} type="number" min="0" value={item.sizes[s] ?? ""} placeholder="0" inputMode="numeric"
                  onChange={(e) => setSize(s, e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value) || 0))}
                  className={`w-full bg-transparent text-center text-2xl font-extrabold tabular-nums focus:outline-none ${active ? "text-foreground" : "text-muted-foreground/60"}`} />
                <div className="flex items-center gap-2">
                  <button type="button" data-testid={`size-dec-${idx}-${s}`} aria-label={`mniej ${s}`} onClick={() => setSize(s, Math.max(0, cur(s) - 1))}
                    className="h-7 w-7 grid place-items-center rounded-full bg-secondary text-muted-foreground hover:text-foreground active:scale-90 transition-transform"><Minus className="h-3.5 w-3.5" strokeWidth={3} /></button>
                  <button type="button" data-testid={`size-inc-${idx}-${s}`} aria-label={`więcej ${s}`} onClick={() => setSize(s, cur(s) + 1)}
                    className="h-7 w-7 grid place-items-center rounded-full bg-amber-400 text-[#0D1117] hover:bg-amber-500 active:scale-90 transition-transform shadow-sm"><Plus className="h-3.5 w-3.5" strokeWidth={3} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {surcharge && (
        <div data-testid={`item-surcharge-${idx}`} className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-3 py-2.5 text-xs font-medium text-sky-200">
          <span className="font-bold">Dopłata rozmiarowa (netto):</span>{" "}
          {Object.entries(surcharge).map(([s, v], i) => (
            <span key={s} className={cur(s) > 0 ? "font-extrabold text-sky-100 underline" : ""}>{i > 0 ? " · " : ""}{s} +{v} zł</span>
          ))}
          <span className="mt-1 block opacity-80">Wpisz cenę podstawową sprzedaży — dopłatę za większy rozmiar dodaj dopiero w WAPRO MAG (nie wpływa na prowizję).</span>
        </div>
      )}

      <div className="grid sm:grid-cols-[200px_1fr] gap-3 items-start rounded-2xl border border-border bg-background/40 p-3" data-testid={`item-branding-${idx}`}>
        <div>
          <Label className="text-xs font-semibold">Technika znakowania</Label>
          <Select value={br.type} onValueChange={setBrandingType}>
            <SelectTrigger data-testid={`item-branding-type-${idx}`} className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{bTypes.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {br.type !== "Same metki" && (
          <div>
            <Label className="text-xs font-semibold">Umiejscowienie</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {pOpts.map((p) => (
                <label key={p} className="flex items-center gap-2 rounded-md border border-border bg-secondary px-2.5 py-1.5 text-xs cursor-pointer has-[:checked]:border-amber-400 has-[:checked]:bg-amber-400/10">
                  <Checkbox data-testid={`item-placement-${idx}-${p.replace(/\s/g, "-")}`} checked={(br.placements || []).includes(p)} onCheckedChange={() => togglePlacement(p)} />{p}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs font-semibold">Uwagi do pozycji</Label>
        <Textarea data-testid={`item-notes-${idx}`} value={item.notes || ""} onChange={(e) => onChange({ ...item, notes: e.target.value })}
          placeholder="np. grafika A — logo na piersi; inna wizualizacja niż pozycja powyżej" rows={2} className="mt-1 text-sm" />
      </div>

      <div>
        <Label className="text-xs font-semibold">Wizualizacja (do produkcji)</Label>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {selectedViz && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1.5" data-testid={`item-viz-selected-${idx}`}>
              <button type="button" onClick={() => setPreviewViz(selectedViz)} title="Podgląd wizualizacji" className="shrink-0">
                {(selectedViz.front_image_id || selectedViz.base_image_id) ? <img src={`${API}/files/${selectedViz.front_image_id || selectedViz.base_image_id}`} alt="" className="h-9 w-9 rounded object-contain bg-[#0D1117]" /> : <ImageIcon className="h-5 w-5" />}
              </button>
              <div className="min-w-0">
                <p className="max-w-[180px] truncate text-xs font-medium">{selectedViz.title_text || selectedViz.title || "Wizualizacja"}</p>
                {selectedViz.subtitle_text && <p className="max-w-[180px] truncate text-[10px] text-muted-foreground">{selectedViz.subtitle_text}</p>}
              </div>
              <button type="button" data-testid={`item-viz-preview-${idx}`} onClick={() => setPreviewViz(selectedViz)} title="Podgląd" className="text-sky-400 hover:text-sky-300"><Eye className="h-3.5 w-3.5" /></button>
              <button type="button" data-testid={`item-viz-clear-${idx}`} onClick={() => onChange({ ...item, viz_id: null })} className="text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <Popover open={vizOpen} onOpenChange={setVizOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm" data-testid={`item-viz-pick-${idx}`} className="gap-1.5 border-dashed"><ImageIcon className="h-4 w-4" />{selectedViz ? "Zmień wizualizację" : "Wybierz wizualizację"}</Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Szukaj wizualizacji…" />
                <CommandList>
                  <CommandEmpty>Brak zapisanych wizualizacji.</CommandEmpty>
                  <CommandGroup>
                    {vizOptions.map((v) => (
                      <CommandItem key={v.id} value={`${v.title_text || ""} ${v.subtitle_text || ""} ${v.title || ""}`} data-testid={`item-viz-option-${idx}-${v.id}`} onSelect={() => { onChange({ ...item, viz_id: v.id }); setVizOpen(false); }}>
                        {(v.front_image_id || v.base_image_id) ? <img src={`${API}/files/${v.front_image_id || v.base_image_id}`} alt="" className="mr-2 h-8 w-8 rounded object-contain bg-[#0D1117]" /> : <ImageIcon className="mr-2 h-4 w-4" />}
                        <div className="min-w-0 flex-1"><p className="truncate text-xs font-medium">{v.title_text || v.title || "Wizualizacja"}</p>{v.subtitle_text && <p className="truncate text-[10px] text-muted-foreground">{v.subtitle_text}</p>}</div>
                        <span role="button" data-testid={`item-viz-option-preview-${idx}-${v.id}`} onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setPreviewViz(v); }} className="ml-1 text-sky-400 hover:text-sky-300"><Eye className="h-4 w-4" /></span>
                        {item.viz_id === v.id && <Check className="ml-2 h-4 w-4 text-amber-500" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <Dialog open={!!previewViz} onOpenChange={(o) => !o && setPreviewViz(null)}>
        <DialogContent data-testid={`item-viz-preview-dialog-${idx}`} className="max-w-3xl">
          {previewViz && (
            <div className="space-y-3">
              <DialogTitle className="font-display text-sm font-bold uppercase tracking-wider">{previewViz.title_text || previewViz.title || "Wizualizacja"}{previewViz.subtitle_text ? ` — ${previewViz.subtitle_text}` : ""}</DialogTitle>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[["front_image_id", "Przód"], ["back_image_id", "Tył"], ["side_image_id", "Bok"]].filter(([k]) => previewViz[k]).map(([k, lab]) => (
                  <div key={k} className="rounded-lg border border-border bg-[#0D1117] p-2">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{lab}</p>
                    <img src={`${API}/files/${previewViz[k]}`} alt={lab} className="max-h-[60vh] w-full object-contain" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isHaft && (
        <div data-testid={`item-haft-note-${idx}`} className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs font-medium text-amber-200">
          {!haftCustom && <>
            <span className="font-bold">Haft (netto):</span> przód {haft.przod} zł · tył {haft.tyl} zł · przód+tył {haft.oba} zł.
            {haftAdd > 0
              ? <span data-testid={`item-haft-applied-${idx}`} className="mt-1 block font-bold text-amber-100">Wybrane umiejscowienie → doliczono +{haftAdd} zł/szt. do ceny (nie wpływa na prowizję).</span>
              : <span className="mt-1 block opacity-80">Zaznacz „przód" i/lub „tył" w polu „Umiejscowienie", aby doliczyć haft do ceny.</span>}
          </>}
          <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-background/40 px-3 py-2">
            <div>
              <p className="text-xs font-bold text-amber-100">Niestandardowa cena haftu</p>
              <p className="text-[10px] opacity-75">Dolicza stałą kwotę do ceny końcowej — niezależnie od miejsca znakowania i prowizji.</p>
            </div>
            <button type="button" role="switch" aria-checked={haftCustom} data-testid={`item-haft-custom-toggle-${idx}`} onClick={() => setHaftCustom(!haftCustom)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${haftCustom ? "bg-amber-400" : "bg-muted-foreground/30"}`}>
              <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${haftCustom ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>
          {haftCustom && <>
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs text-amber-100">Cena haftu / szt. (netto):</Label>
              <input type="number" min="0" step="0.01" data-testid={`item-haft-custom-price-${idx}`} value={br.haft_custom_price || ""} onChange={(e) => setHaftCustomPrice(e.target.value)} className="h-8 w-28 rounded border border-input bg-background px-2 text-sm font-semibold" placeholder="np. 50" />
              <span className="text-xs opacity-75">zł</span>
            </div>
            <span data-testid={`item-haft-applied-${idx}`} className="mt-1 block font-bold text-amber-100">Niestandardowa cena haftu → doliczono +{pln(haftAdd)}/szt. do ceny (nie wpływa na prowizję).</span>
          </>}
          <span className="mt-1 block opacity-80">Przygotowanie logo do haftu: bezpłatne od 10 szt.; poniżej 10 szt. jednorazowa opłata 100 zł netto (FLRA-340, MOONTEX-ICE). Haft rozliczasz osobno — nie wpływa na prowizję.</span>
        </div>
      )}

      {isAdmin && (
        <div data-testid={`item-custom-comm-${idx}`} className="rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-3 py-2.5">
          <label htmlFor={`item-custom-comm-toggle-${idx}`} className="flex items-center gap-2 text-xs font-bold text-indigo-200 cursor-pointer select-none">
            <Checkbox id={`item-custom-comm-toggle-${idx}`} data-testid={`item-custom-comm-toggle-${idx}`} checked={customComm} onCheckedChange={(v) => setCustomComm(!!v)} />
            Indywidualne ustalenie prowizji
          </label>
          <p className="mt-1 text-[10px] text-indigo-200/70">Tylko dla tego zamówienia — ręcznie ustal prowizję/szt. dla tej pozycji. Nie zmienia globalnych progów prowizji.</p>
          {customComm && (
            <div className="mt-2 flex items-center gap-2">
              <Label className="text-xs text-indigo-100">Prowizja / szt. (netto):</Label>
              <input type="number" min="0" step="0.01" data-testid={`item-custom-comm-price-${idx}`} value={item.custom_commission_per_piece ?? ""} onChange={(e) => setCustomCommPrice(e.target.value)} className="h-8 w-28 rounded border border-input bg-background px-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400" placeholder="np. 2.50" />
              <span className="text-xs opacity-75">zł</span>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 grid sm:grid-cols-5 gap-4" data-testid={`item-calc-${idx}`}>
        <div><p className="font-mono text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Sztuki</p><p className="font-display text-2xl font-extrabold tabular-nums">{qty}</p></div>
        <div><p className="font-mono text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Cena pełna / szt.</p>
          <p data-testid={`item-unit-total-${idx}`} className="font-display text-2xl font-extrabold tabular-nums text-foreground">{pln(unitTotal)}</p>
          {haftAdd > 0 && <p className="text-[10px] font-semibold text-amber-300/80">{pln(price)} + {pln(haftAdd)} haft</p>}</div>
        <div><p className="font-mono text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Wartość netto</p><p data-testid={`item-net-${idx}`} className="font-display text-2xl font-extrabold tabular-nums">{pln(qty * unitTotal)}</p></div>
        <div><p className="font-mono text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Prowizja / szt.</p>
          <p data-testid={`item-commission-${idx}`} className={`font-display font-extrabold tabular-nums ${effCommNote ? "text-rose-300 text-base leading-7" : customComm ? "text-indigo-300 text-2xl" : "text-amber-300 text-2xl"}`}>{effCommNote || pln(effCommPP)}</p>
          {customComm && <p className="text-[10px] font-semibold text-indigo-300/80">indywidualna</p>}</div>
        <div><p className="font-mono text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Prowizja razem</p><p className="font-display text-2xl font-extrabold tabular-nums text-amber-300">{pln(qty * effCommPP)}</p></div>
        <div className="sm:col-span-5 mt-1 border-t border-amber-500/25 pt-3 flex flex-wrap gap-2">
          {tierChips(item.sku, rules).map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background/70 px-2.5 py-1 text-sm font-bold">
              <span className="text-muted-foreground">{t.threshold}</span>
              <span className="text-muted-foreground">→</span>
              <span className="text-amber-300">{t.reward}</span>
            </span>
          ))}
        </div>
      </div>
      </>)}
    </div>
  );
};
